/**
 * Drain worker — replays queued writes when the device comes back online.
 *
 * Triggered by:
 *   • window `online` event (instant retry the moment connection returns)
 *   • 30-second poll (catches edge cases where `online` event misses,
 *     particularly on iOS Safari where it's sometimes flaky)
 *   • Manual `drainQueueNow()` from the status drawer
 *
 * Replays in FIFO order, one at a time, with backoff inherent in
 * the polling. Failed replays revert to "pending" so the next tick
 * picks them up.
 *
 * Safety guards:
 *   • The Idempotency-Key on every record means the backend dedups
 *     replays — no double-create.
 *   • 4xx responses mark the record `failed` (manual intervention).
 *   • 5xx / network errors stay `pending` (will retry next tick).
 *   • A single `draining` flag prevents two drains running at once.
 */
import { idbAll } from "./idb";
import { markSyncing, markSynced, markPending, markFailed } from "./queue";

let draining = false;

export const drainQueueNow = async () => {
  if (draining) return { drained: 0, skipped: "already_draining" };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { drained: 0, skipped: "offline" };
  }
  draining = true;
  let drained = 0;
  try {
    const all = await idbAll();
    for (const item of all) {
      if (item.status !== "pending") continue;
      const ok = await syncOne(item);
      if (ok) drained += 1;
    }
  } finally {
    draining = false;
  }
  return { drained };
};

async function syncOne(record) {
  await markSyncing(record.id);
  try {
    const res = await fetch(record.url, {
      method: record.method,
      headers: { ...record.headers, "Content-Type": "application/json" },
      body: record.body != null ? JSON.stringify(record.body) : undefined,
      // Don't send cookies — auth is JWT in the Authorization header.
      credentials: "omit",
    });
    if (res.ok || res.status === 409 /* idempotency replay */) {
      await markSynced(record.id);
      return true;
    }
    if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
      // 4xx (except 408 Request Timeout & 429 Rate Limited) = permanent.
      const errText = await res.text().catch(() => "");
      await markFailed(record.id, `HTTP ${res.status}: ${errText.slice(0, 200)}`);
      return false;
    }
    // 5xx / 408 / 429 → leave pending, will retry next drain.
    await markPending(record.id, `HTTP ${res.status}`);
    return false;
  } catch (err) {
    // Network error / fetch threw → leave pending, retry next tick.
    await markPending(record.id, err?.message || "network error");
    return false;
  }
}

// ────────────────────────────────────────────────────────────────────
// Auto-start: listen for `online` and poll.
// Idempotent — calling startDrainWorker multiple times is safe.
// ────────────────────────────────────────────────────────────────────
let workerStarted = false;
export const startDrainWorker = () => {
  if (workerStarted || typeof window === "undefined") return;
  workerStarted = true;

  window.addEventListener("online", () => {
    drainQueueNow().catch(() => {});
  });

  // Poll every 30s — light enough not to drain battery, frequent
  // enough that a stuck queue clears within half a minute of recovery.
  setInterval(() => {
    if (navigator.onLine) drainQueueNow().catch(() => {});
  }, 30_000);

  // Best-effort initial drain in case the page loads with stuff
  // already in the queue and we're already online.
  if (navigator.onLine) drainQueueNow().catch(() => {});
};
